import React, { useState, useMemo } from "react";
import { Button, IconButton, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useNavigate } from "react-router-dom";
import { useGetOrdersQuery } from "state/api";
import {
  PageContainer,
  DataTable,
  TableToolbar,
  StatusChip,
  normalizeList,
  formatCurrency,
  formatDateTime,
} from "components/shared";

// One filterable page replacing the former all/pending/confirmed/delivered
// pages. Status is driven server-side via ?status=; the tabs simply set it.
const STATUS_TABS = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Confirmed", value: "confirmed" },
  { label: "Processing", value: "processing" },
  { label: "Shipped", value: "shipped" },
  { label: "Delivered", value: "delivered" },
  { label: "Cancelled", value: "cancelled" },
];

const OrdersPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0); // 0-based for TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { data, isFetching } = useGetOrdersQuery({
    status,
    search,
    page: page + 1,
    limit: rowsPerPage,
  });

  const { rows, total } = useMemo(
    () => normalizeList(data, { page: page + 1, pageSize: rowsPerPage }),
    [data, page, rowsPerPage]
  );

  const columns = useMemo(
    () => [
      {
        field: "invoiceNumber",
        headerName: "Invoice",
        render: (o) => o.invoiceNumber || o._id?.slice(-6).toUpperCase(),
      },
      {
        field: "createdAt",
        headerName: "Date",
        render: (o) => formatDateTime(o.createdAt),
      },
      {
        field: "customer",
        headerName: "Customer",
        render: (o) => o.customer?.name || "Walk-in",
      },
      {
        field: "total",
        headerName: "Total",
        align: "right",
        render: (o) => formatCurrency(o.total),
      },
      {
        field: "paymentMethod",
        headerName: "Payment",
        render: (o) => (o.paymentMethod || "—").toUpperCase(),
      },
      {
        field: "paymentStatus",
        headerName: "Paid",
        render: (o) => <StatusChip status={o.paymentStatus} />,
      },
      {
        field: "status",
        headerName: "Status",
        render: (o) => <StatusChip status={o.status} />,
      },
      {
        field: "actions",
        headerName: "",
        align: "right",
        render: (o) => (
          <Tooltip title="View details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/orders/order-details?id=${o._id}`);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [navigate]
  );

  const exportCsv = () => {
    const header = ["Invoice", "Date", "Customer", "Total", "Payment", "Paid", "Status"];
    const lines = rows.map((o) =>
      [
        o.invoiceNumber || o._id?.slice(-6),
        new Date(o.createdAt).toISOString(),
        o.customer?.name || "Walk-in",
        o.total,
        o.paymentMethod,
        o.paymentStatus,
        o.status,
      ].join(",")
    );
    const blob = new Blob([[header.join(","), ...lines].join("\n")], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "orders.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageContainer
      title="Orders"
      subtitle="Track and manage every order across all statuses"
    >
      <TableToolbar
        tabs={STATUS_TABS}
        tabValue={status}
        onTabChange={(v) => {
          setStatus(v);
          setPage(0);
        }}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        searchPlaceholder="Search by invoice or order number…"
        actions={
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={exportCsv}
            disabled={rows.length === 0}
          >
            Export
          </Button>
        }
      />
      <DataTable
        columns={columns}
        rows={rows}
        loading={isFetching}
        emptyText="No orders match the current filters"
        onRowClick={(o) => navigate(`/orders/order-details?id=${o._id}`)}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
      />
    </PageContainer>
  );
};

export default OrdersPage;
